import axios from "axios";
import environments from "../environment";
import FindLocation from "../models/FindLocation";
import { DefaultModel } from "../models/IModel";
import Location, { LocationDetail } from "../models/Location";
import { ActiveType } from "../models/Login";
import { PriceDetail } from "../models/Order";
import User, { PaymentMethod } from "../models/User";
import { ImageInputData } from "../views/hooks/useImageUploadInput";
import http from "./http";

const COOKIE_POLICY_PUBLIC_PATHS = [
	"/utility/cookie-content",
	"/utility/cookie-policy-content",
];

async function fetchCookiePolicyContent() {
	for (const path of COOKIE_POLICY_PUBLIC_PATHS) {
		try {
			const { data } = await http.get(path);
			return { data: data.content };
		} catch (error) {
			if (!axios.isAxiosError(error) || error.response?.status !== 404) {
				throw error;
			}
		}
	}

	return { data: "" };
}

async function fetchCookiePolicySsrContent() {
	for (const path of COOKIE_POLICY_PUBLIC_PATHS) {
		try {
			const { data } = await axios.get(
				(environments.localApiRoot || environments.apiRoot) + path,
				{
					timeout: environments.localApiTimeout || undefined,
					headers: {},
				}
			);
			return data;
		} catch (error) {
			if (!axios.isAxiosError(error) || error.response?.status !== 404) {
				throw error;
			}
		}
	}

	return null;
}

export const MyUserDetail = async () => {
	const { data } = await http.get(`/my-user-detail`);

	const res = new User().parse(data);

	return res;
};

export async function getListUserService(
	inPage: number,
	inPerPage: number,
	phone?: string,
	email?: string,
	banned?: string | null
): Promise<{
	data: User[];
	count: number;
}> {
	const params: any = {
		...(inPerPage && { perPage: inPerPage || 50 }),
		...(inPage && { page: inPage || 1 }),
		...(phone && { phone: phone }),
		...(email && { email: email }),
		...(banned === "true" || banned === "false" ? { banned } : {}),
	};
	let results = await http.get(`/users?${new URLSearchParams(params).toString()}`);
	return {
		data: DefaultModel.parseList<User>(results.data.docs, () => new User()),
		count: results.data.total,
	};
}

export const updateUserDetail = async (fullName: string, phone: string, communicationId: string, avatar: any) => {
	let { data } = await http.put(`/update-user-detail`, {
		allowFullName: true,
		fullName: fullName,
		allowPhone: true,
		phone: phone,
		allowCommunicationId: true,
		communicationId: communicationId,
		allowAvatar: true,
		avatar: avatar,
	});
	return data;
};
export const uploadAvatar = async (avatar?: ImageInputData) => {
	const value = new FormData();
	const uploadUrl = "/gcs-upload-single-image?folder=avatars/users";
	// const uploadUrl = environments.uploadRoot + "/upload/avatar";
	value.append("image", avatar?.domData);
	const { data } = await http.post(uploadUrl, value);
	return data;
};

export const UpdatePass = async (password: string, newPassword: string, confirmPassword: string) => {
	let { data } = await http.put(`/update-password`, {
		password: password,
		newPassword: newPassword,
		confirmPassword: confirmPassword,
	});
	return data;
};

export const ListPartnerSearch = async (search: string) => {
	const { data } = await http.get(`/partner-search?search=${search}&searchLimit=10`);

	const res = new User().parseList(data);

	return res;
};

export type AdminPartnerSearchItem = {
	id: number;
	name: string;
	displayName: string | null;
	active: boolean;
	isBanned: boolean;
};

/**
 * Bản dành cho Admin của ListPartnerSearch.
 *
 * `/partner-search` là endpoint KHÔNG có auth (khách vãng lai gọi được) nên từ 08/2026 nó
 * chỉ trả tên công khai kiểu "Luggage storage De Tham" — gõ tên khách sạn thật vào ô lọc
 * station sẽ không ra gì. `/admin-partner-search` có gác quyền `partners.view`, tìm theo
 * cả tên nội bộ lẫn tên công khai và trả về cả hai.
 */
export const ListPartnerSearchAdmin = async (
	search: string,
	includeInactive?: boolean
): Promise<AdminPartnerSearchItem[]> => {
	// includeInactive: chỉ bật cho ô LỌC đơn (đơn cũ của station đã tắt vẫn phải tra được).
	// Ô chọn station khi TẠO đơn hộ khách để mặc định, không được chọn station đã tắt.
	const { data } = await http.get(
		`/admin-partner-search?search=${encodeURIComponent(search || "")}&searchLimit=10${
			includeInactive ? "&includeInactive=true" : ""
		}`
	);

	return Array.isArray(data) ? data : [];
};

export const ListUserSearch = async (search: string) => {
	const { data } = await http.get(`/user-search?search=${search}&searchLimit=10`);

	const res = new User().parseList(data);

	return res;
};

export const PostUserAdmin = async (
	name: string,
	email: string,
	phone: string,
	pass: string,
	rePass: string,
	selectedCountryCode: string,
	type: string
) => {
	let { data } = await http.post(`/admin-create-user`, {
		fullName: name,
		email: email,
		phone: phone,
		password: pass,
		confirmPassword: rePass,
		phonePrefix: selectedCountryCode,
		communicationId: type,
	});
	return data;
};

export const PutUserAdmin = async (
	id: string,
	name: string,
	email: string,
	phone: string,
	pass: string,
	selectedCountryCode: string,
	type: string
) => {
	let { data } = await http.put(`/user/${id}`, {
		fullName: name,
		email: email,
		phone: phone,
		password: pass,
		phonePrefix: selectedCountryCode,
		communicationId: type,
		allowPassword: pass ? true : false,
		allowPhone: true,
		allowPhonePrefix: true,
		allowFullName: true,
		allowCommunicationId: true,
		allowEmail: true,
	});
	return data;
};

export const getUserDetail = async (id: string) => {
	let results = await http.get(`/user/${id}`);

	const data = new User().parse(results.data);
	return data;
};

export const updateActiveUser = async (id: string, status: boolean) => {
	let results = await http.patch(`/deactive-user/${id}`, {
		active: status ? false : true,
	});

	return results;
};

/** Hard-delete user when API allows (not admin, not self, no booking/payment history). */
export const deleteUserAdmin = async (id: string) => {
	const { data } = await http.delete(`/admin/users/${id}`);
	return data;
};

export const banUserAdmin = async (id: string, reason: string) => {
	const { data } = await http.post(`/admin/users/${id}/ban`, { reason });
	return data;
};

export const unbanUserAdmin = async (id: string, note?: string) => {
	const { data } = await http.post(`/admin/users/${id}/unban`, { note: note ?? "" });
	return data;
};


export const getPaymentMethodByAdmin = async () => {
	let results = await http.get(`/payment-method-by-admin`);

	const data = new PaymentMethod().parseList(results.data);
	return data;
};

export const getPaymentMethodByLanding = async () => {
	let results = await http.get(`/payment-method`);

	const data = new PaymentMethod().parseList(results.data);
	return data;
};


export const postPaymentMethodByAdmin = async (dataIndex: number[]) => {
	let { data } = await http.put(`/payment-method-index`, {
		data: dataIndex
	});
	return data;
};

export const postPaymentMethodStatus = async (id: number, status: number) => {
	let { data } = await http.put(`/payment-method-status/${id}`, {
		status: status
	});
	return data;
};

export const userGetTerm = async (): Promise<{ data: string }> => {
	const { data } = await http.get(`/utility/term-content`);
	return { data: data.content };
};

export const userGetTermOfUse = async (): Promise<{ data: string }> => {
	const { data } = await http.get(`/utility/term-of-use-content`);
	return { data: data.content };
};

export const userGetPrivacyPolicy = async (): Promise<{ data: string }> => {
	const { data } = await http.get(`/utility/privacy-policy-content`);
	return { data: data.content };
};

export const userGetCookiePolicy = async (): Promise<{ data: string }> => {
	return fetchCookiePolicyContent();
};
 
export const userGetContact = async (): Promise<{ data: string }> => {
	const { data } = await http.get(`/utility/contact-content`);
	return {data:data.content};
};
 
export const userGetAbout = async (): Promise<{ data: string }> => {
	const { data } = await http.get(`/utility/about-content`);
	return {data:data.content};
};

export const updateTerm = async (content: string) => {
	let { data } = await http.post(`/utility/admin-term-content`, {
		content: content
	});
	return data;
};

export const updateTermOfUse = async (content: string) => {
	let { data } = await http.post(`/utility/admin-term-of-use-content`, {
		content: content
	});
	return data;
};

export const updatePrivacyPolicy = async (content: string) => {
	let { data } = await http.post(`/utility/admin-privacy-policy-content`, {
		content: content
	});
	return data;
};

export const updateCookiePolicy = async (content: string) => {
	let { data } = await http.post(`/utility/admin-cookie-policy-content`, {
		content: content
	});
	return data;
};

export const updateContact = async (content: string) => {
	let { data } = await http.post(`/utility/admin-contact-content`, {
		content: content
	});
	return data;
};

export const updateAbout = async (content: string) => {
	let { data } = await http.post(`/utility/admin-about-content`, {
		content: content
	});
	return data;
};

export const getTrustItems = async (): Promise<{ trustItems: any[] }> => {
	const { data } = await http.get(`/utility/trust-items`);
	return { trustItems: data.trustItems || [] };
};

export const updateTrustItems = async (trustItems: any[]) => {
	let { data } = await http.post(`/utility/admin-trust-items`, {
		trustItems: trustItems
	});
	return data;
};


export async function getTermSSR(): Promise<{
	dataGetTerm: any;
	rawGetTerm: any;
}> {
	let headers = {};
	const { data } = await axios.get(
		(environments.localApiRoot || environments.apiRoot) + `/utility/term-content`,
		{
			timeout: environments.localApiTimeout || undefined,
			headers,
		}
	);
	return {
		dataGetTerm: data.content,
		rawGetTerm: data,
	};
}

export async function getTermOfUseSSR(): Promise<{
	dataGetTermOfUse: any;
	rawGetTermOfUse: any;
}> {
	let headers = {};
	const { data } = await axios.get(
		(environments.localApiRoot || environments.apiRoot) + `/utility/term-of-use-content`,
		{
			timeout: environments.localApiTimeout || undefined,
			headers,
		}
	);
	return {
		dataGetTermOfUse: data.content,
		rawGetTermOfUse: data,
	};
}

export async function getPrivacyPolicySSR(): Promise<{
	dataGetPrivacyPolicy: any;
	rawGetPrivacyPolicy: any;
}> {
	let headers = {};
	const { data } = await axios.get(
		(environments.localApiRoot || environments.apiRoot) + `/utility/privacy-policy-content`,
		{
			timeout: environments.localApiTimeout || undefined,
			headers,
		}
	);
	return {
		dataGetPrivacyPolicy: data.content,
		rawGetPrivacyPolicy: data,
	};
}

export async function getCookiePolicySSR(): Promise<{
	dataGetCookiePolicy: any;
	rawGetCookiePolicy: any;
}> {
	const data = await fetchCookiePolicySsrContent();
	return {
		dataGetCookiePolicy: data?.content || "",
		rawGetCookiePolicy: data,
	};
}

export async function offlineGetTerm(raw: any): Promise<{ data: string }> {
	let data = raw.content;

	return { data };
}

export async function offlineGetTermOfUse(raw: any): Promise<{ data: string }> {
	let data = raw.content;

	return { data };
}

export async function offlineGetPrivacyPolicy(raw: any): Promise<{ data: string }> {
	let data = raw.content;

	return { data };
}

export async function offlineGetCookiePolicy(raw: any): Promise<{ data: string }> {
	let data = raw.content;

	return { data };
}

export async function getContactSSR(): Promise<{
	dataGetContact: any;
	rawGetContact: any;
}> {
	let headers = {};
	const { data } = await axios.get(
		(environments.localApiRoot || environments.apiRoot) + `/utility/contact-content`,
		{
			timeout: environments.localApiTimeout || undefined,
			headers,
		}
	);
	return {
		dataGetContact: data.content,
		rawGetContact: data,
	};
}

export async function offlineGetContact(raw: any): Promise<{ data: string }> {
	let data = raw.content;

	return { data };
}

export async function getAboutSSR(): Promise<{
	dataGetAbout: any;
	rawGetAbout: any;
}> {
	let headers = {};
	const { data } = await axios.get(
		(environments.localApiRoot || environments.apiRoot) + `/utility/about-content`,
		{
			timeout: environments.localApiTimeout || undefined,
			headers,
		}
	);
	return {
		dataGetAbout: data.content,
		rawGetAbout: data,
	};
}

export async function offlineGetAbout(raw: any): Promise<{ data: string }> {
	let data = raw.content;

	return { data };
}
